import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PrimaryBlueButton,
  SecondaryBlueButton
} from "@velaro/velaro-component-lib";
import { ScraperJob, AzureIndex } from "./types";
import useAzureIndexesApi from "@/hooks/useAzureIndexesApi";

const LAST_INDEX_KEY = "velaro_last_publish_index_id";
const jobPubKey = (jobId: string) => `velaro_job_pub_${jobId}`;

function getPublishedInfo(jobId: string): { indexId: number; indexName: string } | null {
  try {
    const stored = localStorage.getItem(jobPubKey(jobId));
    return stored ? JSON.parse(stored) : null;
  } catch { return null; }
}

function savePublishedInfo(jobId: string, indexId: number, indexName: string) {
  localStorage.setItem(jobPubKey(jobId), JSON.stringify({ indexId, indexName }));
  localStorage.setItem(LAST_INDEX_KEY, String(indexId));
}

function getInitialIndexId(indexes: AzureIndex[], jobId: string): number | null {
  if (indexes.length === 0) return null;
  if (indexes.length === 1) return indexes[0].id;
  // Prefer previously published index for this job, then last-used index
  const jobInfo = getPublishedInfo(jobId);
  if (jobInfo && indexes.some((ix) => ix.id === jobInfo.indexId)) return jobInfo.indexId;
  const stored = localStorage.getItem(LAST_INDEX_KEY);
  if (stored) {
    const id = Number(stored);
    if (indexes.some((ix) => ix.id === id)) return id;
  }
  return indexes[0].id;
}

interface PublishToIndexModalProps {
  job: ScraperJob;
  indexes: AzureIndex[];
  onClose: () => void;
  onPublish: (indexId: number) => Promise<void>;
  onIndexCreated?: (index: AzureIndex) => void;
}

export default function PublishToIndexModal({
  job,
  indexes,
  onClose,
  onPublish,
  onIndexCreated,
}: PublishToIndexModalProps) {
  const { createIndex } = useAzureIndexesApi();
  const publishedInfo = getPublishedInfo(job.job_id);

  const [selectedIndexId, setSelectedIndexId] = useState<number | null>(
    getInitialIndexId(indexes, job.job_id)
  );
  const [publishing, setPublishing] = useState(false);

  // If indexes load asynchronously after modal opens, sync the default selection
  useEffect(() => {
    if (selectedIndexId === null && indexes.length > 0) {
      setSelectedIndexId(getInitialIndexId(indexes, job.job_id));
    }
  }, [indexes]);
  const [newIndexName, setNewIndexName] = useState("main");
  const [creatingIndex, setCreatingIndex] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // When already published, allow switching to a different index
  const [changingIndex, setChangingIndex] = useState(false);

  const selectedIndex = indexes.find((ix) => ix.id === selectedIndexId);
  const noIndexes = indexes.length === 0;
  // Informational banner only — we always render the selector now so users
  // can re-target at any time. Previously the selector was hidden until the
  // "Publish to a different index instead" link was clicked, which users
  // reported as "can't select the actual index anymore".
  const showPublishedBanner = !!publishedInfo && !changingIndex;
  const alreadyPublished = showPublishedBanner;

  const handlePublish = async (indexId: number) => {
    setPublishing(true);
    try {
      await onPublish(indexId);
      const ix = indexes.find((i) => i.id === indexId);
      savePublishedInfo(job.job_id, indexId, ix?.name ?? "");
      onClose();
    } finally {
      setPublishing(false);
    }
  };

  const handleCreateAndPublish = async () => {
    const name = newIndexName.trim().toLowerCase();
    if (!name) return;
    setCreatingIndex(true);
    setCreateError(null);
    try {
      const result = await createIndex({ name, type: "standard", searchSize: 500, semanticSearchEnabled: true });
      if (!result.success || !result.data) {
        setCreateError(result.error ?? "Failed to create index");
        return;
      }
      onIndexCreated?.(result.data);
      await handlePublish(result.data.id);
    } finally {
      setCreatingIndex(false);
    }
  };

  return (
    <Modal show={true}>
      <ModalHeader title="Publish to Bot Index" onClose={onClose} />
      <ModalBody>
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Publish <strong>{job.name}</strong> ({job.pages_scraped} page{job.pages_scraped !== 1 ? "s" : ""}) so your bot can answer questions from this content.
          </p>

          {/* No indexes — create one inline */}
          {noIndexes && (
            <div className="space-y-3">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm font-medium text-blue-800 mb-1">Create your first index</p>
                <p className="text-xs text-blue-700 mb-3">
                  An index is where your bot's knowledge lives. Most sites only ever need one — you can create more later for separate business units or demo accounts.
                </p>
                <label className="block text-xs font-medium text-blue-800 mb-1">Index name</label>
                <input
                  type="text"
                  value={newIndexName}
                  onChange={(e) => setNewIndexName(e.target.value)}
                  placeholder="main"
                  className="w-full px-3 py-2 border border-blue-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-blue-600 mt-1">Lowercase letters only. "main" works for most sites.</p>
              </div>
              {createError && (
                <p className="text-xs text-red-600">{createError}</p>
              )}
            </div>
          )}

          {/* Already-published informational banner (selector still visible below) */}
          {!noIndexes && showPublishedBanner && publishedInfo && (
            <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-md">
              <span className="text-green-600 text-base mt-0.5">✓</span>
              <div>
                <p className="text-sm font-medium text-green-800">
                  Previously published to <strong>{publishedInfo.indexName}</strong>
                </p>
                <p className="text-xs text-green-700 mt-0.5">
                  Re-publish to sync the latest content, or pick a different index below.
                </p>
              </div>
            </div>
          )}

          {/* Index selector — always visible when indexes exist */}
          {!noIndexes && (
            indexes.length === 1 ? (
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-md flex items-center gap-2 text-sm text-slate-700">
                <span className="text-base">📦</span>
                <span>Publishing to <strong>{indexes[0].name}</strong></span>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Select index
                </label>
                <select
                  value={selectedIndexId ?? ""}
                  onChange={(e) => {
                    setSelectedIndexId(Number(e.target.value));
                    // Any manual change flips us out of the "already published" shortcut
                    setChangingIndex(true);
                  }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  {indexes.map((index) => (
                    <option key={index.id} value={index.id}>
                      {index.name}
                    </option>
                  ))}
                </select>
              </div>
            )
          )}

          {!noIndexes && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-xs text-blue-700">
                Your bot's knowledge updates within a few minutes of publishing.
              </p>
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <SecondaryBlueButton label="Cancel" onClick={onClose} />

        {noIndexes ? (
          <PrimaryBlueButton
            label={creatingIndex ? "Creating & Publishing..." : "Create Index & Publish"}
            onClick={handleCreateAndPublish}
            disabled={!newIndexName.trim() || creatingIndex}
          />
        ) : alreadyPublished ? (
          <PrimaryBlueButton
            label={publishing ? "Re-publishing..." : "Re-publish"}
            onClick={() => handlePublish(selectedIndexId ?? publishedInfo!.indexId)}
            disabled={publishing}
          />
        ) : (
          <PrimaryBlueButton
            label={publishing ? "Publishing..." : "Publish"}
            onClick={() => handlePublish(selectedIndexId ?? indexes[0].id)}
            disabled={!selectedIndexId || publishing}
          />
        )}
      </ModalFooter>
    </Modal>
  );
}
